import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import Buyers from '@/store/modules/buyers';
import Acl from '@/store/modules/acl';
import { deepClone, isEmpty } from '@/main';
import UserProfileService from '@/service/UserProfileService';
import UserPreferenceService from '@/service/UserPreferenceService';
import { getKeycloak } from '@/service/KeycloakService';

export const initialState = {
  selectedPeriod: null,
  comparedPeriod: null,
  reloadUnmatchedBuyers: null,
  selectedPortfolio: null,
  selectedPortfolioIsChanged: false,
  indexOfNewSelectedPortfolio: null,
  periods: [],
  notifications: [],
  notificationsCount: 0,
  loadingInterval: null,
  user: null,
  editing: false,
  loading: 0,
  comparedPeriodDefaultMonths: 1,
  portfolioTypes: [],
  risks: {
    veryLow: { color: '#479d72', hoverColor: '#4fb37e' },
    low: { color: '#99cc40', hoverColor: '#a7d911' },
    avgLow: { color: '#9be990', hoverColor: '#eaf6df' },
    avgHigh: { color: '#d4f1a5', hoverColor: '#f8f8aa' },
    high: { color: '#ff9900', hoverColor: '#fca421' },
    veryHigh: { color: '#ff0000', hoverColor: '#ec2f2f' },
  },
  progressLoaded: 30,
  progressTotal: 0,
  showDialogEditManager: false,
  fromRoute: null,
  restorePortfolio: null,
  userPreferenceSaved: false,
  newPortfolio: false,
};

const store = createStore({
  modules: {
    buyers: Buyers,
    acl: Acl,
  },
  state: deepClone(initialState),
  getters: {
    reloadUnmatchedBuyers: state => state.reloadUnmatchedBuyers,
    selectedPeriod: state => state.selectedPeriod,
    comparedPeriod: state => state.comparedPeriod,
    comparedPeriodDefaultMonths: state => state.comparedPeriodDefaultMonths,

    periods: state => state.periods,
    newPortfolio: state => state.newPortfolio,
    userPreferenceSaved: state => state.userPreferenceSaved,
    indexOfNewSelectedPortfolio: state => state.indexOfNewSelectedPortfolio,
    confirmationDialogEmailSent: state => state.confirmationDialogEmailSent,
    user: state => state.user,
    userRole: state => (state.user ? state.user.role : null),
    userRoles: state => (state.user ? state.user.roles : null),
    userAgencyCode: state => (state.user ? state.user.agencyCode : null),
    restorePortfolio: state => state.restorePortfolio,
    selectedPortfolioIsChanged: state => state.selectedPortfolioIsChanged,
    portfolioList: state => {
      if (state.user != null && state.user.portfolioList != null) {
        return state.user.portfolioList;
      }
      return [];
    },
    portfolioTypes: state => state.portfolioTypes,
    loading: state => state.loading > 0,
    editing: state => state.editing,
    notifications: state => state.notifications,
    selectedPortfolio: state => {
      if (state.selectedPortfolio != null) {
        if (state.selectedPortfolio.customer == null)
          state.selectedPortfolio.customer = {};
        if (state.selectedPortfolio.buyers == null) {
          state.selectedPortfolio.buyers = [];
        }
        state.selectedPortfolio.selectioInsured = state.selectedPortfolio.policyNumber != null;
      }
      if (state.selectedPortfolio == null)
        state.selectedPortfolio = { id: 0, customer: {} };
      return state.selectedPortfolio;
    },
    token: state => {
      if (state.user != null) {
        return state.user.token;
      }
      return null;
    },
    showDialogEditManager: state => state.showDialogEditManager,
    fromRoute: state => state.fromRoute,
    risks: state => state.risks,
    progress: state => state.progressLoaded,
    notificationsCount: state => state.notificationsCount,
    companyCode: state => state.user.companyCode,
  },
  mutations: {
    RESET_STATE_INDEX(state) {
      Object.assign(state, initialState)
    },
    setReloadUnmatchedBuyers(state, value) {
      state.reloadUnmatchedBuyers = value;
    },
    setDivisionUser(state, value) {
      state.user.division = value;
    },
    setUserPreferenceSaved(state, value) {
      state.userPreferenceSaved = value;
    },
    setIndexOfNewSelectedPortfolio(state, value) {
      state.indexOfNewSelectedPortfolio = value;
    },
    setSelectedPortfolioIsChanged(state, value) {
      state.selectedPortfolioIsChanged = value;
    },
    setConfirmationDialogEmailSent(state, value) {
      state.confirmationDialogEmailSent = value;
    },
    restoreState(state, newState) {
      Object.assign(state, newState);
    },
    setSelectedPortfolio(state, selectedPortfolio) {
      if (selectedPortfolio == null) {
        selectedPortfolio = { id: 0, customer: {} };
      } else if (selectedPortfolio.customer == null)
        selectedPortfolio.customer = {};
      state.selectedPortfolio = selectedPortfolio;
    },
    setRestorePortfolio(state, restorePortfolio) {
      state.restorePortfolio = restorePortfolio;
    },
    setContracts(state, contracts) {
      if (contracts == null) {
        contracts = [];
      }
      state.contracts = contracts;
    },
    setUser(state, user) {
      state.user = user;
    },
    setPortfolioList(state, list) {
      console.error('setPortfolioList', list);
      state.user.portfolioList = list;
    },
    setNewPortfolio(state, value) {
      state.newPortfolio = value;
    },
    setToken(state, token) {
      if (state.user != null) {
        state.user.token = token;
      }
    },
    setEditing(state, editing) {
      state.editing = editing;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setPeriods(state, periods) {
      state.periods = periods;
    },
    setSelectedPeriod(state, selectedPeriod) {
      state.selectedPeriod = selectedPeriod;
    },
    setComparedPeriod(state, comparedPeriod) {
      state.comparedPeriod = comparedPeriod;
    },
    setPortfolioTypes(state, portfolioTypes) {
      state.portfolioTypes = portfolioTypes;
    },
    setProgressLoaded(state, progressLoaded) {
      state.progressLoaded = (isNaN(progressLoaded) ? 0 : progressLoaded);
    },
    setProgressTotal(state, progressTotal) {
      state.progressTotal = (isNaN(progressTotal) ? 0 : progressTotal);
    },
    incrementProgressLoaded(state, progressLoaded) {
      state.progressLoaded += (isNaN(progressLoaded) ? 0 : progressLoaded);
    },
    incrementProgressTotal(state, progressTotal) {
      state.progressTotal += (isNaN(progressTotal) ? 0 : progressTotal);
    },
    setLoadingInterval(state, loadingInterval) {
      state.loadingInterval = loadingInterval;
    },
    setNotifications(state, notifications) {
      state.notifications = notifications;
    },
    setNotificationsCount(state, notificationsCount) {
      state.notificationsCount = notificationsCount;
    },
    setShowDialogEditManager(state, showDialogEditManager) {
      state.showDialogEditManager = showDialogEditManager;
    },
    setFromRoute(state, fromRoute) {
      state.fromRoute = fromRoute;
    },
  },
  actions: {
    startProgress(context) {
      if (context.state.progressLoaded < 70) {
        setTimeout(() => {
          context.commit('setProgressLoaded', 70);
        }, 1000);
      }
      if (context.state.loadingInterval == null) {
        context.state.loadingInterval = setInterval(() => {
          if (context.state.loading === 0) {
            clearInterval(context.state.loadingInterval);
            context.commit('setLoadingInterval', null);
          } else {
            context.commit('incrementProgressLoaded', 2);
          }
        }, 500);
      }
    },
    startLoading(context) {
      context.commit('setLoading', context.state.loading + 1);
      context.dispatch('startProgress');
    },
    stopLoading(context) {
      context.commit('setProgressLoaded', 0);
      context.commit('setProgressTotal', 100);
      if (context.state.loading === 1) {
        clearInterval(context.state.loadingInterval);
        context.commit('setLoadingInterval', null);
        context.commit('setProgressLoaded', 100);
        setTimeout(() => {
          context.commit('setLoading', context.state.loading > 0 ? context.state.loading - 1 : 0);
          context.commit('setProgressLoaded', 0);
        }, 800);
      } else {
        context.commit('setLoading', context.state.loading > 0 ? context.state.loading - 1 : 0);
      }
    },
    resetLoading(context) {
      clearInterval(context.state.loadingInterval);
      context.commit('setLoadingInterval', null);
      context.commit('setLoading', 0);
      context.commit('setProgressLoaded', 0);
      context.commit('setProgressTotal', 0);
    },
    async logout(context) {
      context.commit('restoreState', initialState);
    },
    async login(context, { vm, user }) {
      context.commit('setUser', user);
      await context.dispatch('acl/createAclRules', { vm, user });
    },

    /**
     * Carica i dati utente da IAM e prepara l'ambiente come faceva il vecchio login.
     */
    async loadUserFromIam({ state, commit, dispatch }) {
      const userProfileService = new UserProfileService();
      const userPreferenceService = new UserPreferenceService();
      const kc = getKeycloak();
      const normalizeRole = (role) => {
        if (!role) return '';
        const raw = String(role).trim().toLowerCase();
        const map = {
          'selectio_dir': 'selectiodir',
          'selectio-dir': 'selectiodir',
          'selectio backoffice': 'selectiodir',
          'selectioage': 'selectioage',
          'selectio_age': 'selectioage',
          'selectio-age': 'selectioage',
          'selectiocli': 'selectiocli',
          'selectio_cli': 'selectiocli',
          'selectio-cli': 'selectiocli',
          'selectiobro': 'selectiobro',
          'selectio_bro': 'selectiobro',
          'selectio-bro': 'selectiobro',
          'selectio-capo-gruppo': 'selectio-capo-gruppo',
          'selectio_capo_gruppo': 'selectio-capo-gruppo',
          'selectiocapogruppo': 'selectio-capo-gruppo',
        };
        if (map[raw]) return map[raw];
        const compact = raw.replace(/\s+/g, '');
        if (map[compact]) return map[compact];
        return raw;
      };
      const toStringOrEmpty = (value) => (value == null ? '' : String(value));

      try {
        // 1. Scarica Anagrafica (include profiles)
        const userInfo = await userProfileService.getUserInfo();
        const tokenParsed = kc?.tokenParsed || {};
        console.log("IAM User Info Raw:", userInfo);

        // 2. Determina il profilo attivo (replica logica Backend SsoController)
        const profiles = Array.isArray(userInfo.profiles)
          ? userInfo.profiles
          : (Array.isArray(userInfo.profile) ? userInfo.profile : []);
        const selectioProfiles = profiles.filter(p => {
          const code = p?.role?.code || p?.role || '';
          return String(code).toLowerCase().includes('selectio');
        });
        let activeProfile = {};
        if (selectioProfiles.length > 0) {
          // Prende quello 'main' tra i profili Selectio, o il primo Selectio se nessuno è main
          activeProfile = selectioProfiles.find(p => p.main) || selectioProfiles[0];
        } else if (profiles.length > 0) {
          // Fallback: main generico o primo profilo disponibile
          activeProfile = profiles.find(p => p.main) || profiles[0];
        }
        console.log("Active Profile Selected:", activeProfile);

        // 3. Normalizza il ruolo per l'ACL
        // acl.js si aspetta stringhe esatte come 'selectiodir', 'selectiocli', etc.
        // Assumiamo che IAM restituisca activeProfile.role.code.
        const rawRoleCode = activeProfile.role && typeof activeProfile.role === 'object'
          ? activeProfile.role.code
          : (activeProfile.role || '');
        const normalizedRole = normalizeRole(rawRoleCode);

        // 4. Mappatura campi critici per il Frontend
        // Qui avviene la magia che mancava: mappiamo i campi IAM ai campi legacy che l'app si aspetta.
        const username = userInfo.username || userInfo.userName || userInfo.preferredUsername ||
          tokenParsed.preferred_username || tokenParsed.email || tokenParsed.upn || '';
        const email = userInfo.email || tokenParsed.email || '';
        const roles = profiles.length > 0
          ? profiles.map(p => normalizeRole(p.role?.code || p.role)).filter(Boolean)
          : (Array.isArray(tokenParsed.realm_access?.roles)
            ? tokenParsed.realm_access.roles.map(normalizeRole).filter(Boolean)
            : []);
        const fullUser = {
          ...userInfo,
          ...activeProfile,
          username,
          email,

          // Forza i ruoli
          roles,
          role: normalizedRole || roles.find(r => r.startsWith('selectio')) || roles[0] || '',

          // Mappatura Company Code (Assicura che sia stringa per confronti === '328')
          companyCode: toStringOrEmpty(
            activeProfile.companyCode ?? activeProfile.companyId ??
            userInfo.companyCode ?? userInfo.companyId,
          ),

          // Mappatura Token
          token: kc.token,

          // --- MAPPATURA CRITICA PER SELECTIO ---
          // Nel vecchio SsoService: profile.principalCode -> user.customerCode
          customerCode: activeProfile.principalCode || activeProfile.customerCode ||
            userInfo.customerCode || userInfo.codiceContraente || userInfo.principalCode || '',
          // Nel vecchio SsoService: profile.agencyCode -> user.agencyCode
          agencyCode: activeProfile.agencyCode || userInfo.agencyCode || userInfo.codAge3 || '',

          // Flag servizi abilitati (se disponibili)
          selectioEnabled: Boolean(userInfo.isSelectioEnabled ?? userInfo.selectioEnabled),
          cofastEnabled: Boolean(userInfo.isCofastEnabled ?? userInfo.cofastEnabled),
          lensEnabled: Boolean(userInfo.isLensEnabled ?? userInfo.lensEnabled),
          databrainEnabled: Boolean(userInfo.isDatabrainEnabled ?? userInfo.databrainEnabled),
        };

        // Gestione specifica ruolo 'selectio-capo-gruppo'
        const groupCode = activeProfile.groupCode || userInfo.groupCode;
        if (fullUser.role === 'selectio-capo-gruppo' && groupCode) {
           // I capi gruppo usano il codice gruppo come customerCode nei filtri
          fullUser.customerCode = groupCode;
        }

        console.log("Full User Object Prepared:", fullUser);

        // 5. CARICAMENTO PORTAFOGLI (Logica replicata da old main.js)
        let portfolioList = [];
        const params = [];
        const hasVal = (v) => !isEmpty(v);

        // Costruzione parametri di ricerca basata sul ruolo normalizzato
        if (fullUser.role === 'selectiodir') {
          if (hasVal(fullUser.username) && hasVal(fullUser.companyCode)) {
            params.push({ fieldId: 'username', match: 'eq', value: fullUser.username });
            params.push({ fieldId: 'companyId', match: 'eq', value: fullUser.companyCode });
          }
        }
        else if (fullUser.role === 'selectiocli' || fullUser.role === 'selectiobro') {
          if (hasVal(fullUser.customerCode) && hasVal(fullUser.companyCode)) {
            params.push({ fieldId: 'customerCode', match: 'eq', value: fullUser.customerCode });
            params.push({ fieldId: 'username', match: 'eq', value: fullUser.username });
            params.push({ fieldId: 'companyId', match: 'eq', value: fullUser.companyCode });
          }
        }
        else if (fullUser.role === 'selectio-capo-gruppo') {
          if (hasVal(fullUser.customerCode) && hasVal(fullUser.companyCode)) {
            // Nota: qui customerCode contiene già il groupCode grazie alla mappatura sopra
            params.push({ fieldId: 'groupCode', match: 'eq', value: fullUser.customerCode });
            params.push({ fieldId: 'username', match: 'eq', value: fullUser.username });
            params.push({ fieldId: 'companyId', match: 'eq', value: fullUser.companyCode });
          }
        }
        else if (fullUser.role === 'selectioage') {
          if (hasVal(fullUser.agencyCode) && hasVal(fullUser.companyCode)) {
            params.push({ fieldId: 'agencyCode', match: 'eq', value: fullUser.agencyCode });
            params.push({ fieldId: 'username', match: 'eq', value: fullUser.username });
            params.push({ fieldId: 'companyId', match: 'eq', value: fullUser.companyCode });
          }
        }

        if (params.length > 0) {
          try {
            console.log("Fetching portfolios with params:", params);
            portfolioList = await userPreferenceService.searchUserPortfolios(params);
          } catch (err) {
            console.error("[Store] Errore caricamento portafogli:", err);
          }
        } else {
            console.warn("[Store] Nessun parametro valido per ricerca portafogli. Ruolo:", normalizedRole);
        }

        fullUser.portfolioList = portfolioList;

        const persistedSelectedPortfolioId = Number(state.selectedPortfolio?.id);
        const hasPersistedSelectedPortfolio =
          Number.isFinite(persistedSelectedPortfolioId) && persistedSelectedPortfolioId > 0;

        // 6. Salva nello Store
        commit('setUser', fullUser);
        if (hasPersistedSelectedPortfolio) {
          const restoredSelectedPortfolio = portfolioList.find((portfolio) =>
            Number(portfolio.id) === persistedSelectedPortfolioId,
          ) || null;
          commit('setSelectedPortfolio', restoredSelectedPortfolio);
        }
        if (kc?.token) {
          commit('setToken', kc.token);
        }
        if (kc) {
          kc.onAuthRefreshSuccess = () => {
            if (kc.token) commit('setToken', kc.token);
          };
          kc.onAuthLogout = () => {
            commit('restoreState', initialState);
          };
        }

        // 7. Seleziona il primo portafoglio (Scatena caricamento Dashboard)
        // if (portfolioList.length > 0) {
        //     commit('setSelectedPortfolio', portfolioList[0]);
        // }

        // 8. Genera ACL (Genera i menu)
        // vm: null va bene solo se hai applicato la fix a acl.js che usa i18n importato
        await dispatch('acl/createAclRules', { vm: null, user: fullUser });
        console.log("ACL Rules created.");

      } catch (error) {
        console.error("[Store] Errore critico loadUserFromIam:", error);
        throw error;
      }
    }
  },
  plugins: [createPersistedState()],
});

export default store;
